import type { BackendTypes } from '@tf/api';
import { createStyles, Stack, Table } from '@tf/ui';
import type React from 'react';

import { useConfigContext } from '../../../hooks';

import { getExperienceCellKind } from './getExperienceCellKind';
import { CheckboxInput, NumberInput, SelectInput, TextInput } from './index';

const useStyles = createStyles(({ colors }) => ({
	tableStack: {
		padding: 0,
	},
	tableStyles: {
		'& th, & td': {
			borderColor: `${colors.gray[2]} !important`,
		},
		'& td:first-of-type': {
			minWidth: '180px',
		},
	},
	infoField: {
		'> span': {
			fontWeight: 300,
		},
	},
}));

const ROW_NAME = 'Financial instrument';

interface ExperienceInputProps {
	structDef: BackendTypes.StructDefinition;
	segmentName: string;
	className: string;
	isReadonly?: boolean;
	isScoreScale?: boolean;
}

export const ExperienceInput: React.FC<ExperienceInputProps> = ({
	structDef,
	className,
	segmentName,
	isReadonly,
	isScoreScale,
}) => {
	const { getStructDefinition, currency } = useConfigContext((s) => s);
	const { classes } = useStyles();

	// every struct field in this case has the same ref
	const columns = structDef.fields[0].ref
		? getStructDefinition(structDef.fields[0].ref).fields.map((field) => field.visual)
		: [];

	const hasTooltips = columns.some(({ tooltip }) => tooltip);

	const renderCell = (field: BackendTypes.FieldDefinition, name: string) => {
		const cellKind = getExperienceCellKind(field, isScoreScale);

		if (cellKind === 'boolean') {
			return <CheckboxInput isReadOnly={isReadonly} name={name} />;
		}

		if (cellKind === 'score') {
			return <NumberInput isReadOnly={isReadonly} name={name} min={0} max={5} placeholder={'Enter value (0-5)'} />;
		}

		if (cellKind === 'money') {
			return <NumberInput isReadOnly={isReadonly} name={name} currency={currency} />;
		}

		if (cellKind === 'enum') {
			return <SelectInput isReadOnly={isReadonly} name={name} enumName={field.ref} />;
		}

		if (cellKind === 'text') {
			return <TextInput isReadOnly={isReadonly} name={name} placeholder={field.visual.placeholder} />;
		}

		return <NumberInput isReadOnly={isReadonly} name={name} placeholder={field.visual.placeholder || 'Enter value'} />;
	};

	return (
		<Stack className={`${className} ${classes.tableStack}`}>
			<Table fs="sm" className={classes.tableStyles} withColumnBorders striped>
				<thead>
					<tr>
						<th rowSpan={hasTooltips ? 2 : 1}>{ROW_NAME}</th>
						{columns.map(({ label }) => (
							<th key={label}>{label}</th>
						))}
					</tr>
					{hasTooltips && (
						<tr>
							{columns.map(({ label, tooltip }) => (
								<th className={classes.infoField} key={label}>
									<span>{tooltip}</span>
								</th>
							))}
						</tr>
					)}
				</thead>
				<tbody>
					{structDef.fields.map((struct) => {
						if (!struct.ref) return null;
						const primitiveFields = getStructDefinition(struct.ref).fields;

						return (
							<tr key={struct.visual.label}>
								<td>{struct.visual.label}</td>
								{primitiveFields.map((field) => {
									const name = `${segmentName}.${struct.name}.${field.name}`;
									return <td key={name}>{renderCell(field, name)}</td>;
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>
		</Stack>
	);
};
